<template>
  <main
    class="notifications-page"
    role="main"
    aria-labelledby="notifications-h1"
  >
    <div class="page-header">
      <h1
        id="notifications-h1"
        class="h1"
      >
        Meldungen
      </h1>
      <div

        class="actions"
      >
        <button
          class="btn btn-primary"
          aria-labelledby="notifications-h1"
          @click="openCreate = true"
        >
          Neu
        </button>
      </div>
    </div>

    <div class="card">
      <!-- Filterleiste (analog Groups) -->
      <div class="filters">
        <div class="field field--from">
          <label for="ntf-date-from">Datum von</label>
          <FlatPickr
            id="ntf-date-from"
            v-model="dateFromFilter"
            class="flatpickr-input"
            :config="datePickerConfig"
            placeholder="DD.MM.YYYY"
          />
        </div>

        <div class="field field--to">
          <label for="ntf-date-to">Datum bis</label>
          <FlatPickr
            id="ntf-date-to"
            v-model="dateToFilter"
            class="flatpickr-input"
            :config="datePickerConfig"
            placeholder="DD.MM.YYYY"
          />
        </div>

        <div class="field field--search">
          <label for="ntf-search">Schülersuche</label>
          <input
            id="ntf-search"
            v-model="q"
            class="field"
            placeholder="Schüler oder Betreff"
            autocomplete="off"
            @keyup.enter="/* clientseitig */ null"
          >
        </div>

        <div
          class="field field--status"
        >
          <label for="ntf-status">Status</label>
          <select
            id="ntf-status"
            v-model="statusFilter"
          >
            <option value="">
              Alle
            </option>
            <option value="offen">
              Offen
            </option>
            <option value="gelesen">
              Gelesen
            </option>
            <option value="abgelehnt">
              Abgelehnt
            </option>
            <option value="akzeptiert">
              Akzeptiert
            </option>
          </select>
        </div>

        <div class="field field--chat">
          <label for="ntf-chat">Nachrichten</label>
          <select
            id="ntf-chat"
            v-model="chatFilter"
          >
            <option value="">
              Alle
            </option>
            <option value="unread">
              ungelesen
            </option>
            <option value="read">
              gelesen
            </option>
          </select>
        </div>

        <div class="field field--reset">
          <button
            class="btn btn-primary d-inline"
            :disabled="!hasActiveFilters"
            @click="resetFilters"
          >
            Reset
          </button>
        </div>
      </div>

      <div>
        <!-- Tabellen-Header (analog Groups) -->
        <div class="notifications-table">
          <div :class="['table-header', isParent ? 'column-5' : 'column-7']">
            <div
              class="sortable"
              role="button"
              tabindex="0"
              @click="setSort('studentName')"
              @keydown.enter="setSort('studentName')"
            >
              Schüler
              <span v-if="sortKey === 'studentName'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
            </div>
            <div>Betreff</div>
            <div v-if="!isParent">
              Absender
            </div>
            <div
              class="sortable"
              role="button"
              tabindex="0"
              @click="setSort('sentAt')"
              @keydown.enter="setSort('sentAt')"
            >
              Gesendet
              <span v-if="sortKey === 'sentAt'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
            </div>
            <div>Datum</div>
            <div>
              Status
            </div>
            <div class="text-right">
              Aktion
            </div>
          </div>
          <!-- Zeilen -->
          <div
            v-for="item in pagedItems"
            :key="item.id"
            :class="['table-row', isParent ? 'column-5' : 'column-7']"
          >
            <div>
              <strong>{{ item.studentName }}</strong>
              <i
                v-if="hasChat(item) > 0"
                :class="['pi','pi-envelope','notif-icon', unreadCount(item) > 0 ? 'notif-unread-icon' : 'notif-readed-icon']"
                aria-hidden="true"
                title="Neue Nachrichten"
              />
            </div>
            <div class="muted subject">
              {{ item.subject || '' }}
              <i
                v-if="item.hasAttachment"
                class="pi pi-file-pdf"
                aria-hidden="true"
                style="font-size:14px;color:var(--danger);vertical-align: baseline;"
              />
              <i
                v-if="hasCorrections(item)"
                class="pi pi-clone"
                aria-hidden="true"
                :title="'Korrekturverlauf vorhanden'"
                style="font-size:12px;color:var(--primary);margin-left:6px;vertical-align: baseline;"
              />
            </div>
            <div
              v-if="!isParent"
              class="muted"
            >
              {{ senderOf(item) }}
            </div>
            <div class="muted">
              {{ formatDateTime(item.sentAt) }}
            </div>
            <div>{{ formatDateRange(item) }}</div>
            <div>
              <span
                class="badge"
                :class="badgeClass(moderationStatus(item))"
              >{{ statusLabel(moderationStatus(item)) }}</span>
            </div>
            <div class="row-actions">
              <button
                class="icon-btn open-view"
                :class="[unreadCount(item) > 0 ? 'notif-unread' : '']"
                :title="'Meldung ansehen'"
                @click="openView(item)"
              >
                <i
                  v-if="unreadCount(item) > 0"
                  class="pi pi-envelope"
                  aria-hidden="true"
                  title="Neue Nachrichten"
                />
                <i
                  v-else
                  class="pi pi-eye"
                  aria-hidden="true"
                  style="font-size:18px;color:var(--muted);"
                />
              </button>
              <button
                v-if="isAdmin || (isTeacher && isCreatorOf(item))"
                class="icon-btn"
                :title="'Korrekturmeldung erstellen'"
                @click="createCorrection(item)"
              >
                <i
                  class="pi pi-pencil"
                  aria-hidden="true"
                  style="font-size:18px;color:var(--muted);"
                />
              </button>

              <button
                v-if="canParentDelete(item)"
                class="icon-btn danger"
                title="Meldung löschen"
                aria-label="Meldung löschen"
                @click="openDeleteConfirm(item)"
              >
                <i
                  class="pi pi-trash"
                  aria-hidden="true"
                  style="font-size:18px;"
                />
              </button>
            </div>
          </div>

          <!-- Skeleton -->
          <div
            v-if="loading && !isParent"
            class="table-row column-7 skeleton"
          >
            <div>
              <div class="sk-line" />
              <div class="sk-line short" />
            </div>
            <div>
              <div class="sk-line" />
            </div>
            <div>
              <div class="sk-line" />
            </div>
            <div>
              <div class="sk-line short" />
            </div>
            <div>
              <div class="sk-line short" />
            </div>
            <div>
              <div class="sk-badge" />
            </div>
            <div class="text-right">
              <div class="sk-btn" />
            </div>
          </div>
          <div
            v-if="loading && isParent"
            class="table-row column-5 skeleton"
          >
            <div>
              <div class="sk-line" />
              <div class="sk-line short" />
            </div>
            <div>
              <div class="sk-line" />
            </div>
            <div>
              <div class="sk-line short" />
            </div>
            <div>
              <div class="sk-line short" />
            </div>
            <div class="text-right">
              <div class="sk-btn" />
            </div>
          </div>
        </div>

        <!-- Mobile: Cards statt Tabelle -->
        <div
          class="notifications-cards"
        >
          <div
            v-for="item in pagedItems"
            :key="`card-${item.id}`"
            class="notif-card"
          >
            <div class="notif-card__top">
              <div class="notif-card__title">
                <strong class="notif-card__student">{{ item.studentFirstName }}</strong>
                <i
                  v-if="hasChat(item) > 0"
                  :class="['pi','pi-envelope','notif-icon', unreadCount(item) > 0 ? 'notif-unread-icon' : 'notif-readed-icon']"
                  aria-hidden="true"
                  title="Neue Nachrichten"
                />
              </div>

              <span
                class="badge notif-card__status"
                :class="badgeClass(moderationStatus(item))"
              >{{ statusLabel(moderationStatus(item)) }}</span>
            </div>

            <div class="notif-card__subject">
              <span
                class="muted"
                style="font-size: 15px; font-weight: 500;"
              >{{ item.subject || '—' }}</span>
              <span class="notif-card__icons">
                <i
                  v-if="item.hasAttachment"
                  class="pi pi-file-pdf"
                  aria-hidden="true"
                  style="font-size:14px;color:var(--danger);vertical-align: baseline;"
                />
                <i
                  v-if="hasCorrections(item)"
                  class="pi pi-clone"
                  aria-hidden="true"
                  :title="'Korrekturverlauf vorhanden'"
                  style="font-size:12px;color:var(--primary);margin-left:6px;vertical-align: baseline;"
                />
              </span>
            </div>

            <div class="notif-card__meta">
              <div class="meta">
                <span>
                  <span class="muted font-semibold">für: </span>{{ formatDateRange(item) }}</span>
              </div>
            </div>

            <div class="notif-card__actions-wrapper">
              <div
                class="meta"
                style="line-height: 16px;"
              >
                <span><span class="muted font-semibold">Gesendet: </span>{{ formatDateTime(item.sentAt) }}</span>
                <span class="d-block"><span class="muted font-semibold">von: </span>{{ senderOf(item) }}</span>
              </div>
              <div class="notif-card__actions">
                <button
                  class="btn btn-primary notif-card__open d-inline"
                  :class="[unreadCount(item) > 0 ? 'notif-card__open--unread' : '']"
                  @click="openView(item)"
                >
                  <i
                    v-if="unreadCount(item) > 0"
                    class="pi pi-envelope"
                    aria-hidden="true"
                    title="Neue Nachrichten"
                  />
                  <i
                    v-else
                    class="pi pi-eye"
                    aria-hidden="true"
                  />
                </button>

                <button
                  v-if="canParentDelete(item)"
                  class="btn danger"
                  type="button"
                  @click="openDeleteConfirm(item)"
                >
                  <i
                    class="pi pi-trash"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        <p
          v-if="!loading && filteredItems.length === 0"
          class="muted"
        >
          Keine Einträge.
        </p>
        <p
          v-if="error"
          class="notice notice-danger mt-12"
          role="alert"
        >
          {{ error }}
        </p>
        <p
          v-if="error"
          class="notice notice-danger mt-12"
          role="alert"
        >
          {{ error }}
        </p>
      </div>

      <!-- Pagination (analog Groups) -->
      <div
        class="mt-12"
        style="display:flex;align-items:center;gap:8px;justify-content:flex-end;"
      >
        <span
          class="muted pagination-item"
          style="margin-right:auto"
        >Gesamt: {{ filteredItems?.length ?? 0 }}</span>
        <label
          class="muted pagination-item"
          for="ntf-limit"
        >pro Seite:</label>
        <select
          id="ntf-limit"
          class="pagination-item"
          :value="limit"
          @change="changeLimit"
        >
          <option
            v-for="n in [10,20,50,100]"
            :key="n"
            :value="n"
          >
            {{ n }}
          </option>
        </select>
        <button
          class="btn"
          :disabled="page<=1"
          @click="prevPage"
        >
          Zurück
        </button>
        <span class="muted">Seite {{ page }} / {{ pages }}</span>
        <button
          class="btn"
          :disabled="page>=pages"
          @click="nextPage"
        >
          Weiter
        </button>
      </div>
    </div>

    <BaseDialog
      :open="deleteOpen"
      :disabled="deleting"
      @close="closeDeleteConfirm"
    >
      <template #title>
        Meldung löschen
      </template>

      <div>
        <p style="margin:0 0 10px 0;">
          Möchten Sie diese Meldung wirklich löschen?
        </p>
        <p
          v-if="deleteError"
          class="notice notice-danger mt-12"
          role="alert"
        >
          {{ deleteError }}
        </p>
      </div>

      <div class="actions mt-12">
        <button
          type="button"
          class="btn"
          :disabled="deleting"
          @click="closeDeleteConfirm"
        >
          Abbrechen
        </button>
        <button
          type="button"
          class="btn btn-primary danger-primary"
          :disabled="deleting"
          @click="confirmDelete"
        >
          {{ deleting ? 'Löschen…' : 'Löschen' }}
        </button>
      </div>
    </BaseDialog>

    <!-- Dialog: Neue Meldung (ausgelagert) -->
    <NotificationCreateDialog
      :open="openCreate"
      :notif-parent-id="notifParentId"
      :prefill-student-id="prefill.studentId"
      :prefill-subject="prefill.subject"
      :prefill-date-from="prefill.dateFrom"
      :prefill-date-to="prefill.dateTo"
      :prefill-time-from="prefill.timeFrom"
      :prefill-time-to="prefill.timeTo"
      :prefill-kind="prefill.kind"
      @close="onCreateDialogClose"
      @created="onCreated"
    />
    <NotificationViewDialog
      :open="viewOpen"
      :notification="selected"
      @close="closeView"
      @accept="$emit('accept', $event)"
      @reject="$emit('reject', $event)"
      @create-correction="onCreateCorrection"
    />
    <!-- Top-Navigation: Hilfe-Button -->
    <teleport to=".top-nav">
      <span
        id="top-nav-help-notifications"
        class="top-nav-help"
      >
        <UiInfoButton title="Hilfe: Meldungen">
          <ul>
            <li v-if="isAdmin">Filter: Suche (Schüler/Betreff) und Status (für nicht-Eltern sichtbar).</li>
            <li v-if="isAdmin">Spalten variieren je nach Rolle; Datum und Zeitraum werden komprimiert angezeigt.</li>
            <li>Aktion: Meldung im Detail ansehen; Anhänge sind mit einem Umschlag-Icon gekennzeichnet.</li>
            <li>Paginierung unten steuert die sichtbare Anzahl pro Seite und Navigation.</li>
            <li v-if="isAdmin">Erstellen ist für Admins und Eltern verfügbar.</li>
          </ul>
        </UiInfoButton>
      </span>
    </teleport>
  </main>
</template>

<script setup>
import {onMounted, onBeforeUnmount, ref, computed, watch, nextTick} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import {fetchNotifications, deleteNotification} from '@/api/notifications';
import NotificationCreateDialog from '@/components/notifications/NotificationCreateDialog.vue';
import NotificationViewDialog from "@/components/notifications/NotificationViewDialog.vue";
import {useToast} from 'primevue/usetoast';
import {useAuthStore} from '@/stores/auth';
import api from '@/services/api';
import UiInfoButton from '@/components/UiInfoButton.vue';
import BaseDialog from '@/components/BaseDialog.vue';
// Flatpickr (Design & Locale)
import FlatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import {German} from 'flatpickr/dist/l10n/de.js';

const auth = useAuthStore();
const roleId = computed(() => auth.user?.roleId ?? auth.user?.role_id ?? null);
const isAdmin = computed(() => roleId.value === 1);
const isParent = computed(() => roleId.value === 3);
const currentUserId = computed(() => auth.user?.id ?? null);

const route = useRoute();
const router = useRouter();

const loading = ref(false);
const error = ref('');
const items = ref([]); // alle Einträge von API
const children = ref([]); // Kinder des Elternteils (für RoleId=3)
defineEmits(['accept', 'reject']);
const viewOpen = ref(false);
const selected = ref(null);

// Datumsfilter (Flatpickr)
const dateFromFilter = ref(null);
const dateToFilter = ref(null);
const datePickerConfig = {
  altInput: true,
  altFormat: 'd.m.Y',
  dateFormat: 'Y-m-d', // Backend-freundlich
  allowInput: true,
  locale: German,
  disableMobile: true
};

function toYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fridayOfWeek(d) {
  // Woche = Mo–So (ISO-Logik), gesucht: Freitag derselben Woche wie d
  const base = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const isoDow = (base.getDay() + 6) % 7; // Mo=0 ... So=6
  const monday = new Date(base);
  monday.setDate(base.getDate() - isoDow);
  const friday = new Date(monday);
  friday.setDate(monday.getDate() + 4);
  return friday;
}

function setDefaultLast30Days() {
  const today = new Date();
  const to = fridayOfWeek(today); // <- Datum bis = Freitag der aktuellen Woche
  const from = new Date(to);
  from.setDate(to.getDate() - 30);

  dateFromFilter.value = toYmd(from);
  dateToFilter.value = toYmd(to);
}

// Initial: letzte 30 Tage (inkl. heute)
setDefaultLast30Days();

const hasActiveFilters = computed(() => {
  return Boolean(
      (q.value && q.value.trim() !== '') ||
      (statusFilter.value && statusFilter.value !== '') ||
      (chatFilter.value && chatFilter.value !== '') ||
      (dateFromFilter.value) ||
      (dateToFilter.value)
  );
});

function openView(n) {
  selected.value = n;
  viewOpen.value = true;
}

function closeView() {
  viewOpen.value = false;
  selected.value = null;
  // Nach Schließen des Detail-Dialogs Liste neu laden
  load();
}

function onCreateDialogClose() {
  openCreate.value = false;
  // Nach Schließen des Erstellen-Dialogs Liste neu laden
  load();
}

// Filter
const q = ref('');
const statusFilter = ref('');
const chatFilter = ref('');

// Sortierung
const sortKey = ref('sentAt');
const sortDir = ref('desc');

// Pagination
const page = ref(1);
const limit = ref(20);

function setSort(key) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    sortDir.value = key === 'sentAt' ? 'desc' : 'asc';
  }
}

function nextPage() {
  if (page.value < pages.value) page.value++;
}

function prevPage() {
  if (page.value > 1) page.value--;
}

function changeLimit(e) {
  limit.value = Number(e.target.value);
  page.value = 1;
}

function resetFilters() {
  q.value = '';
  statusFilter.value = '';
  chatFilter.value = '';
  setDefaultLast30Days();
  page.value = 1;
  // Nach Reset neu laden (Server-Zeitfilter wieder auf Default)
  load();
}

async function load() {
  loading.value = true;
  error.value = '';
  try {
    const params = {};
    // Datumsbereich an Backend übergeben (optional)
    if (dateFromFilter.value) params.date_from = dateFromFilter.value;
    if (dateToFilter.value) params.date_to = dateToFilter.value;

    // Eltern: nur eigene Meldungen laden (falls aktiv)
    // if (isParent.value && currentUserId.value) {
    //   params.parent_user_id = currentUserId.value;
    // }
    const data = await fetchNotifications(params);
    items.value = Array.isArray(data) ? data : [];
    page.value = 1;
    // Unread-Infos kommen jetzt pro Item vom Server (threadId/unreadCount/hasUnread)
  } catch (e) {
    error.value = extractMessage(e) || 'Laden der Meldungen fehlgeschlagen.';
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  // Initial laden
  load();
  if (isParent.value) loadChildren();

  // Wenn mit ?new=1 aufgerufen wurde und Rolle erstellen darf → Dialog öffnen
  const wantsNew = route?.query?.new === '1' || route?.query?.new === 'true';
  if (wantsNew && (isAdmin.value || isParent.value)) {
    openCreate.value = true;
    // Query-Flag einmalig verbrauchen
    router.replace({name: route.name || 'Notifications', query: {...route.query, new: undefined}});
  }
});

watch(isParent, (v) => {
  if (v) loadChildren();
});

// Bei Datumsänderung neu laden und Seite zurücksetzen
watch([dateFromFilter, dateToFilter], () => {
  page.value = 1;
  load();
});

// Kinder für Eltern (roleId=3) laden
async function loadChildren() {
  try {
    const {data} = await api.get('/parent/children-status');
    const list = Array.isArray(data) ? data : (data?.children || []);
    children.value = list
        .map(k => ({
          id: k.id ?? k.childId ?? k.uuid ?? k.name ?? null,
          name: k.name ?? k.fullName ?? ''
        }))
        .filter(k => k.id || k.name);
  } catch (e) {
    error.value = extractMessage(e) || 'Kinderzuordnung konnte nicht geladen werden.';
    children.value = [];
  }
}

// Hilfe-Button vor .profile einsortieren
onMounted(() => {
  const nav = document.querySelector('.top-nav');
  const el = document.getElementById('top-nav-help-notifications');
  if (!nav || !el) return;
  const profile = nav.querySelector('.profile');
  if (profile && profile.parentNode === nav) nav.insertBefore(el, profile);
  else nav.appendChild(el);
});

onBeforeUnmount(() => {
  const el = document.getElementById('top-nav-help-notifications');
  if (el && el.parentNode) el.parentNode.removeChild(el);
});

function extractMessage(e) {
  return e?.response?.data?.message || e?.message || '';
}

// Rolle Lehrer (roleId=2) zusätzlich ermitteln
const isTeacher = computed(() => roleId.value === 2);

// Hilfsfunktionen für Nachrichten-Thread (Eltern-Beziehung einer Meldung)
function getNotifParentId(it) {
  return it?.notifParentId ?? it?.parentId ?? it?.parent_id ?? null;
}

function hasCorrections(it) {
  // Für Blätter: vorhanden, wenn eine übergeordnete Meldung existiert
  return Boolean(getNotifParentId(it));
}

// Unread-Infos: werden serverseitig pro Item geliefert (threadId/unreadCount/hasUnread)

// Unread-Count für den Thread des Items (serverseitig ermittelt)
function unreadCount(it) {
  const n = Number(it?.unreadCount ?? 0);
  if (Number.isFinite(n) && n > 0) return n;
  return it?.hasUnread ? 1 : 0;
}

function hasChat(it) {
  // Backend liefert das jetzt explizit (entscheidend für "alt/gelesen")
  if (typeof it?.hasChat === 'boolean') return it.hasChat;
  if (typeof it?.has_chat === 'boolean') return it.has_chat;
  return false;
}

function rootOf(it) {
  if (!it) return null;
  const byId = new Map(items.value.map(n => [String(n.id), n]));
  let cur = it;
  let guard = 0;
  while (cur && getNotifParentId(cur) && guard++ < 100) {
    const p = byId.get(String(getNotifParentId(cur)));
    if (!p) break;
    cur = p;
  }
  return cur || it;
}

// Datum/Zeitraum-Überlappung prüfen (Client-Fallback)
function overlapsWithDateFilter(it) {
  const hasFrom = !!dateFromFilter.value;
  const hasTo = !!dateToFilter.value;
  if (!hasFrom && !hasTo) return true;

  // Filterfenster [start, end)
  const fStart = hasFrom ? new Date(dateFromFilter.value) : null;
  const fEndBase = hasTo ? new Date(dateToFilter.value) : (hasFrom ? new Date(dateFromFilter.value) : null);

  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n, 0, 0, 0, 0);

  const filterStart = fStart ? startOfDay(fStart) : null;
  const filterEnd = fEndBase ? addDays(startOfDay(fEndBase), 1) : null;

  // Item-Fenster:
  const df = it?.dateFrom ?? it?.date_from ?? null;
  const dt = it?.dateTo ?? it?.date_to ?? null;
  const tf = it?.timeFrom ?? it?.time_from ?? null;
  const tt = it?.timeTo ?? it?.time_to ?? null;

  let itemStart, itemEnd;
  if (tf || tt) {
    // Zeitfenster (wenn to <= from, +1 Tag)
    const tStart = tf ? new Date(tf) : (df ? new Date(df) : null);
    let tEnd = tt ? new Date(tt) : (dt ? new Date(dt) : null);
    if (!tStart && df) itemStart = startOfDay(new Date(df));
    else itemStart = tStart ? new Date(tStart) : null;

    if (!tEnd) {
      const d2 = dt ?? df;
      tEnd = d2 ? addDays(new Date(d2), 1) : null;
    }
    if (tStart && tEnd && tEnd <= tStart) tEnd = addDays(tEnd, 1);

    itemEnd = tEnd ? new Date(tEnd) : null;
  } else {
    // Tagesfenster [date_from 00:00, (date_to|date_from)+1 00:00)
    const d1 = df ? new Date(df) : null;
    const d2 = (dt ? new Date(dt) : (df ? new Date(df) : null));
    itemStart = d1 ? startOfDay(d1) : null;
    itemEnd = d2 ? addDays(startOfDay(d2), 1) : null;
  }

  // Fehlende Grenzen → konservativ anzeigen
  if (!itemStart || !itemEnd) return true;

  // Überlappungsprüfung [a1,a2) und [b1,b2) → a1 < b2 && a2 > b1
  if (filterStart && itemEnd <= filterStart) return false;
  if (filterEnd && itemStart >= filterEnd) return false;
  return true;
}

const filteredItems = computed(() => {
  let base = items.value;

  // Nur aktive Meldungen berücksichtigen
  base = (base || []).filter(isActive);

  // Eltern: Nur Meldungen der eigenen Kinder zeigen
  if (isParent.value) {
    if (!children.value || children.value.length === 0) {
      return [];
    }
    const idSet = new Set(children.value.map(k => String(k.id)).filter(Boolean));
    const nameSet = new Set(children.value.map(k => String(k.name).toLowerCase()).filter(Boolean));

    base = base.filter(n => {
      const sid = n.studentId ?? n.student_id ?? n.student?.id ?? n.studentUUID ?? n.studentUuid ?? null;
      if (sid != null && idSet.has(String(sid))) return true;
      const sname = (n.studentName ?? n.student_name ?? '').toLowerCase();
      if (sname && nameSet.has(sname)) return true;
      return false;
    });
  }

  // Pro Thread (Root) nur das letzte Kind anzeigen:
  const byRoot = new Map();
  for (const it of base) {
    const r = rootOf(it);
    const rid = String(r?.id ?? it?.id ?? '');
    if (!rid) continue;
    if (!byRoot.has(rid)) byRoot.set(rid, []);
    byRoot.get(rid).push(it);
  }
  const timeOf = (n) => {
    const v = n?.sentAt ?? n?.sent_at ?? n?.createdAt ?? n?.created_at ?? null;
    const dt = parseDateLike(v);
    return dt ? dt.getTime() : 0;
  };
  const latestPerRoot = [];
  for (const [, arr] of byRoot) {
    if (!arr.length) continue;
    arr.sort((a, b) => timeOf(a) - timeOf(b));
    latestPerRoot.push(arr[arr.length - 1]);
  }

  // Zeitraumfilter:
  // WICHTIG: Threads mit ungelesenen Nachrichten sollen IMMER sichtbar sein,
  // auch wenn sie außerhalb des Datumsfilters liegen.
  let result = latestPerRoot.filter((n) => {
    const isUnread = hasChat(n) && unreadCount(n) > 0;
    if (isUnread) return true;
    return overlapsWithDateFilter(n);
  });

  // Status
  if (statusFilter.value) {
    result = result.filter(n => moderationStatus(n) === statusFilter.value);
  }

  // Chat-Filter: nur Meldungen mit Chat; dann unread/read unterscheiden
  if (chatFilter.value === 'unread') {
    result = result.filter(n => hasChat(n) && unreadCount(n) > 0);
  } else if (chatFilter.value === 'read') {
    result = result.filter(n => hasChat(n) && unreadCount(n) === 0);
  }

  // Suche
  const needle = q.value.trim().toLowerCase();
  if (!needle) return result;

  return result.filter(n =>
      (n.studentName && n.studentName.toLowerCase().includes(needle)) ||
      (n.subject && String(n.subject).toLowerCase().includes(needle))
  );
});
const sortedItems = computed(() => {
  const arr = [...filteredItems.value];

  arr.sort((a, b) => {
    // 0) Priorität: ungelesene Nachrichten immer ganz nach oben
    const aUnread = hasChat(a) && unreadCount(a) > 0;
    const bUnread = hasChat(b) && unreadCount(b) > 0;
    if (aUnread !== bUnread) return aUnread ? -1 : 1;

    // 1) Status-Priorität: offen/gelesen zuerst
    const statusPriority = (status) => {
      if (status === 'offen') return 3;
      if (status === 'gelesen') return 2;
      return 1; // abgelehnt, akzeptiert, sonstige
    };

    const aPrio = statusPriority(moderationStatus(a));
    const bPrio = statusPriority(moderationStatus(b));

    if (aPrio !== bPrio) {
      return bPrio - aPrio; // höhere Priorität zuerst
    }

    // 2) Innerhalb derselben Gruppe: nach Gesendet-Datum DESC
    const va = a.sentAt ? new Date(a.sentAt).getTime() : 0;
    const vb = b.sentAt ? new Date(b.sentAt).getTime() : 0;
    return vb - va; // neuere zuerst
  });

  return arr;
});

// Pagination (nach Sortierung definiert, damit Abhängigkeiten existieren)
const pages = computed(() => Math.max(1, Math.ceil(sortedItems.value.length / limit.value)));

const pagedItems = computed(() => {
  const start = (page.value - 1) * limit.value;
  return sortedItems.value.slice(start, start + limit.value);
});

// Wenn Text-/Status-/Chat-Filter ändern: immer auf Seite 1 springen
watch([q, statusFilter, chatFilter], () => {
  page.value = 1;
});

// Seitenzahl einklemmen, falls sich Trefferanzahl ändert
watch(pages, (p) => {
  if (page.value > p) page.value = p;
  if (page.value < 1) page.value = 1;
});

// Dialog-Steuerung
const openCreate = ref(false);

// Prefill-Hinweis: Werte werden aus der Quell-Meldung in das 'prefill'-Objekt übernommen.

// Event aus dem View-Dialog: Korrektur starten (View vorher schließen)
async function onCreateCorrection(payload) {
  const item = payload || selected.value;
  // View-Dialog schließen und einen Tick warten, damit das Overlay verschwindet
  viewOpen.value = false;
  await nextTick();
  // Danach Korrektur-Dialog öffnen
  createCorrection(item);
}

// Prefill/Parent für Korrektur (Thread-Bezug der Meldung)
const notifParentId = ref(null);
const prefill = ref({
  studentId: null,
  subject: '',
  dateFrom: null,
  dateTo: null,
  timeFrom: null,
  timeTo: null,
  kind: 'daily'
});

function createCorrection(item) {
  const root = rootOf(item);
  notifParentId.value = root?.id ?? null;

  // Prefills aus der Root-Meldung übernehmen
  prefill.value.studentId = root?.studentId ?? root?.student_id ?? null;
  prefill.value.subject = root?.subject ?? '';
  const df = root?.dateFrom ?? root?.date_from ?? null;
  const dt = root?.dateTo ?? root?.date_to ?? null;
  const tf = root?.timeFrom ?? root?.time_from ?? null;
  const tt = root?.timeTo ?? root?.time_to ?? null;
  prefill.value.dateFrom = df || null;
  prefill.value.dateTo = dt || (tf || tt ? df || null : (dt || null));
  prefill.value.timeFrom = tf || null;
  prefill.value.timeTo = tt || null;
  prefill.value.kind = (tf || tt) ? 'time' : 'daily';

  openCreate.value = true;
}

// PrimeVue Toast
const toast = useToast();

async function onCreated(newItem) {
  openCreate.value = false;
  await load(); // komplette Tabelle neu laden
  const who = newItem?.studentName ? ` für ${newItem.studentName}` : '';
  toast.add({severity: 'success', summary: `Meldung${who} gespeichert.`, life: 3500});
}

// Hilfsfunktionen für robuste Datumserkennung/Formatierung
function parseDateLike(value) {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(?:$|T)/.test(value)) {
    const [y, m, d] = value.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function sameYMD(a, b) {
  const da = parseDateLike(a);
  const db = parseDateLike(b);
  if (!da || !db) return false;
  return da.getFullYear() === db.getFullYear()
      && da.getMonth() === db.getMonth()
      && da.getDate() === db.getDate();
}

function formatDate(iso) {
  const dt = parseDateLike(iso);
  if (!dt) return '—';
  const df = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  return df.format(dt);
}

function formatDateTime(iso) {
  const dt = parseDateLike(iso);
  if (!dt) return '—';
  const df = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  return df.format(dt);
}

function formatTime(v) {
  if (!v) return '';
  // Reines HH:MM(:SS) unverändert anzeigen (lokal interpretiert)
  if (typeof v === 'string' && /^(\d{2}):(\d{2})(?::\d{2})?$/.test(v)) {
    const m = v.match(/^(\d{2}):(\d{2})/);
    return m ? `${m[1]}:${m[2]}` : '';
  }
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const tf = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  return tf.format(d);
}

function formatDateRange(item) {
  const from = item?.dateFrom ?? item?.date_from ?? item?.date ?? null;
  const to = item?.dateTo ?? item?.date_to ?? null;
  const tFrom = item?.timeFrom ?? item?.time_from ?? null;
  const tTo = item?.timeTo ?? item?.time_to ?? null;

  // zeitweise: gleiches Datum + Zeiten vorhanden -> Datum + HH:MM–HH:MM
  if (from && to && sameYMD(from, to) && (tFrom || tTo)) {
    const base = formatDate(from);
    const tf = formatTime(tFrom);
    const tt = formatTime(tTo);
    if (tf && tt) return `${base} ${tf}–${tt}`;
    if (tf) return `${base} ${tf}`;
    if (tt) return `${base} ${tt}`;
    return base;
  }

  // Fallback: nur ein Datum, aber Zeiten vorhanden (zur Sicherheit)
  if (from && (tFrom || tTo)) {
    const base = formatDate(from);
    const tf = formatTime(tFrom);
    const tt = formatTime(tTo);
    if (tf && tt) return `${base} ${tf}–${tt}`;
    if (tf) return `${base} ${tf}`;
    if (tt) return `${base} ${tt}`;
    return base;
  }

  // tageweise oder allgemeine Darstellung
  if (from && to) {
    if (sameYMD(from, to)) return formatDate(from);
    return `${formatDate(from)} – ${formatDate(to)}`;
  }
  if (from) return formatDate(from);
  if (to) return formatDate(to);
  return '—';
}

// Absender (Benutzername bevorzugt) ermitteln
function senderOf(it) {
  return it?.parent_name ?? it?.parentName;
}

// Ersteller einer Meldung ermitteln (robust über gängige Feldnamen)
function creatorUserId(it) {
  if (!it || typeof it !== 'object') return null;
  return it.parentUserId ?? null;
}

function canParentDelete(it) {
  if (!isParent.value) return false;
  if (!isCreatorOf(it)) return false;

  const st = moderationStatus(it);
  if (st === 'akzeptiert' || st === 'abgelehnt') return false;

  const id = it?.id ?? null;
  return id !== null && id !== undefined && String(id) !== '';
}

const deleteOpen = ref(false);
const deleteTarget = ref(null);
const deleting = ref(false);
const deleteError = ref('');

function openDeleteConfirm(it) {
  deleteError.value = '';
  deleteTarget.value = it || null;
  deleteOpen.value = true;
}

function closeDeleteConfirm() {
  if (deleting.value) return;
  deleteOpen.value = false;
  deleteTarget.value = null;
  deleteError.value = '';
}

async function confirmDelete() {
  deleteError.value = '';
  const it = deleteTarget.value;
  const id = it?.id ?? null;

  if (id === null || id === undefined || String(id) === '') {
    deleteError.value = 'Ungültige Meldungs-ID.';
    return;
  }
  if (!canParentDelete(it)) {
    deleteError.value = 'Diese Meldung kann nicht gelöscht werden.';
    return;
  }

  deleting.value = true;
  try {
    await deleteNotification(id);
    deleting.value = false;
    closeDeleteConfirm();
    await load();
    toast.add({severity: 'success', summary: 'Meldung gelöscht.', life: 3500});
  } catch (e) {
    deleteError.value = extractMessage(e) || 'Löschen fehlgeschlagen.';
  } finally {
    deleting.value = false;
  }
}

// Prüfen, ob aktueller Nutzer Ersteller dieser Meldung ist
function isCreatorOf(it) {
  const cid = creatorUserId(it);

  return cid != null && currentUserId.value != null && String(cid) === String(currentUserId.value);
}

// Moderationsstatus (offen/gelesen/abgelehnt/akzeptiert) robust extrahieren
function moderationStatus(it) {
  const raw = String(it?.status ?? it?.state ?? '').toLowerCase().trim();
  const allowed = new Set(['offen', 'gelesen', 'abgelehnt', 'akzeptiert']);
  if (allowed.has(raw)) return raw;
  const alt = String(it?.moderationStatus ?? it?.moderation_status ?? it?.mod_status ?? '').toLowerCase().trim();
  return allowed.has(alt) ? alt : '';
}

// Eintrag ist aktiv, wenn is_active true ist (Default true). Fallback: wenn 'deleted' gesetzt ist → inaktiv.
function isActive(it) {
  if (typeof it?.is_active !== 'undefined') return Boolean(it.is_active);
  if (typeof it?.isActive !== 'undefined') return Boolean(it.isActive);
  // Fallback über 'deleted' (userId des Löschers)
  const del = it?.deleted ?? it?.deleted_by ?? it?.deletedBy ?? null;
  if (del !== null && typeof del !== 'undefined' && del !== false && del !== 0) return false;
  return true;
}

function statusLabel(status) {
  if (status === 'offen') return 'Offen';
  if (status === 'gelesen') return 'Gelesen';
  if (status === 'abgelehnt') return 'Abgelehnt';
  if (status === 'akzeptiert') return 'Akzeptiert';
  return status || '—';
}

function badgeClass(status) {
  return {
    'badge-warn': status === 'offen',
    'badge-info': status === 'gelesen',
    'badge-danger': status === 'abgelehnt',
    'badge-success': status === 'akzeptiert',
  };
}

</script>

<style scoped>
.notifications-page {
  background: var(--bg);
  color: var(--text);
}

.page-header {
  border-bottom: 1px solid var(--border);
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .04);
  margin-bottom: 1rem;
  padding: 12px;
}

.filters {
  display: grid;
  grid-template-columns: 90px 90px auto 150px 160px 70px;
  gap: 8px;
  align-items: end;
  margin-bottom: 20px;
}

/* Form-Felder im Filter – gemäß Styleguide */
.filters .field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 6px;
}

.filters .field label {
  font-size: 12px;
  color: var(--muted);
  margin: 0;
}

.filters .field input,
.filters .field select,
.filters .field .flatpickr-input,
.filters .field .flatpickr-alt-input {
  height: 38px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 0;
}

.filters .field .flatpickr-input[readonly],
.filters .field .flatpickr-alt-input[readonly] {
  background: var(--surface);
}

.filters .field input::placeholder,
.filters .field .flatpickr-alt-input::placeholder {
  color: var(--muted);
}

.filters .field input:focus,
.filters .field select:focus,
.filters .field .flatpickr-alt-input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--focus);
}

.table-header.column-7,
.table-row.column-7 {
  display: grid;
  grid-template-columns: 1fr 1.5fr 1fr 120px 200px 100px 80px;
  gap: 8px;
  align-items: center;
  font-size: 14px;
}

.table-header.column-7 {
  font-size: 12px;
  color: var(--muted);
  padding: 6px 0;
  border-bottom: 1px solid var(--border);
}

.table-row.column-7 {
  padding: 5px 0;
  border-bottom: 1px solid var(--border);
}

.table-header.column-5,
.table-row.column-5 {
  display: grid;
  grid-template-columns: 1fr 1.5fr 100px 190px 100px 80px;
  gap: 8px;
  align-items: center;
  font-size: 14px;
}

.table-header.column-5 {
  font-size: 12px;
  color: var(--muted);
  padding: 6px 0;
  border-bottom: 1px solid var(--border);
}

.table-row.column-5 {
  padding: 5px 0;
  border-bottom: 1px solid var(--border);
}

.subject {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.meta {
  font-size: 12px;
  color: var(--muted);
}

.row-actions {
  display: inline-flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
}

.row-actions .open-view.notif-unread {
  background: var(--info);
  color: white;
  border-color: color-mix(in srgb, var(--info) 90%, black);
}

.row-actions .open-view.notif-unread:hover {
  background: color-mix(in srgb, var(--info) 80%, black);
}

.row-actions.center {
  justify-content: center;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 30px;
}

.text-right {
  text-align: right;
}

.text-center {
  text-align: center;
}

/* Sortierbare Header analog Groups */
.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  text-decoration: underline;
}

/* Buttons/Badges/Icon-Buttons (konsistent) */
.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  width: 36px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  cursor: pointer;
  transition: background-color 140ms, transform 80ms;
}

.icon-btn:hover:not(:disabled) {
  background: color-mix(in srgb, var(--primary) 8%, transparent);
}

.icon-btn:active:not(:disabled) {
  transform: translateY(1px);
}

.icon-btn:disabled {
  opacity: .6;
  cursor: default;
}

.icon-btn.danger {
  border-color: color-mix(in srgb, var(--danger) 45%, var(--border));
  color: var(--danger);
}

.icon-btn.danger:hover:not(:disabled) {
  background: rgba(239, 68, 68, .08);
}

.btn.danger {
  border-color: rgba(239, 68, 68, .45);
  color: var(--danger);
}

.btn.danger:hover:not(:disabled) {
  background: rgba(239, 68, 68, .08);
}

/* Primär-Button im Dialog, aber "danger"-Semantik (token-basiert) */
.danger-primary {
  background: var(--danger);
  border-color: var(--danger);
}

.danger-primary:hover:not(:disabled) {
  background: color-mix(in srgb, var(--danger) 85%, black);
}

/* Unread-Indikator (Umschlag) inline bei Betreff */
.notif-unread-icon {
  color: var(--info);
}

.notif-readed-icon {
  color: var(--surface-400);
}

.notif-icon {
  font-size: 12px;
  margin-left: 6px;
  vertical-align: baseline;
}

.badge {
  display: inline-block;
  font-size: 12px;
  line-height: 16px;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--muted);
}

.badge-success {
  color: var(--success);
  border-color: rgba(16, 185, 129, .5);
  background: rgba(16, 185, 129, .08);
}

.badge-warn {
  color: var(--warn);
  border-color: rgba(245, 158, 11, .5);
  background: rgba(245, 158, 11, .08);
}

.badge-info {
  color: var(--info);
  border-color: rgba(37, 99, 235, .5);
  background: rgba(37, 99, 235, .08);
}

.badge-danger {
  color: var(--danger);
  border-color: rgba(239, 68, 68, .5);
  background: rgba(239, 68, 68, .08);
}

.btn {
  height: 38px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  cursor: pointer;
}

.btn:hover {
  background: color-mix(in srgb, var(--primary) 8%, transparent);
}

.btn-primary {
  background: var(--primary);
  color: #fff;
  border-color: var(--primary);
}

.btn-primary:hover {
  background: var(--primary-600);
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid color-mix(in srgb, var(--primary) 30%, transparent);
  border-top-color: var(--primary);
  border-radius: 50%;
  display: inline-block;
  margin-right: 6px;
  animation: spin .9s linear infinite;
}

.skeleton .sk-line {
  height: 12px;
  background: color-mix(in srgb, var(--bg) 50%, white);
  border-radius: 6px;
}

.skeleton .sk-line.short {
  width: 70%;
}

.skeleton .sk-dot {
  width: 20px;
  height: 20px;
  border-radius: 6px;
  background: color-mix(in srgb, var(--bg) 50%, white);
}

.skeleton .sk-badge {
  width: 64px;
  height: 18px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--bg) 50%, white);
}

.skeleton .sk-btn {
  width: 120px;
  height: 32px;
  border-radius: 8px;
  background: color-mix(in srgb, var(--bg) 50%, white);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Cards (Parent Mobile) */
.notifications-cards {
  display: none;
}

.notif-card {
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  padding: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .04);
}

.notif-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.notif-card__title {
  min-width: 0;
}

.notif-card__student {
  display: inline-block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notif-card__subject {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.notif-card__subject .muted {
  min-width: 0;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notif-card__icons {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  flex: 0 0 auto;
}

.notif-card__meta {
  display: grid;
  gap: 10px;
}

.notif-card__actions-wrapper {
  display: grid;
  grid-template-columns:  auto 90px;
  gap: 8px;
  margin-top: 12px;
  align-items: end;
}

.notif-card__open--unread {
  background: var(--info);
  border-color: var(--info);
}

.notif-card__open--unread:hover {
  background: color-mix(in srgb, var(--info) 80%, black);
}

/* Responsiv */
@media (max-width: 920px) {
  .table-header.column-7,
  .table-row.column-7 {
    grid-template-columns: 1fr 1.5fr 1fr 120px 200px 100px 80px;
  }
}

@media (max-width: 799px) {
  /* Parent: Filter kompakter (2 Spalten), Touch-Ziele größer */
  .notifications-page .filters {
    grid-template-columns: 1fr 1fr;
    align-items: end;
    gap: 6px;
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 12px;
    background: whitesmoke;
  }

  .notifications-page .filters .field--search {
    grid-column: 1 / -1;
  }

  .notifications-page .filters .field--status {
    grid-column: 1 / 2;
  }

  .notifications-page .filters .field--chat {
    grid-column: 2 / 3;
  }

  .notifications-page .filters .field--reset {
    grid-column: 1 / -1;
    align-items: flex-end;
  }

  .notifications-page .filters .field input,
  .notifications-page .filters .field select,
  .notifications-page .filters .field button {
    height: 32px;
    font-size: 14px;
    padding: 0 10px !important;
    width: 100%;
  }

  .notifications-page .pagination-item {
    display: none;
  }

  /* Parent: Tabelle ausblenden, Cards anzeigen */
  .notifications-page .notifications-table {
    display: none;
  }

  .notifications-page .notifications-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 10px;
  }

  /* Generisch: bisherige Mobile-Regeln bleiben für Nicht-Eltern aktiv */
  .table-header.column-7,
  .table-row.column-7 {
    grid-template-columns: 1.6fr 1fr 140px 100px;
  }

  /* Spalten auf Mobile zusammenfalten: Betreff, Absender, Aktion ausblenden */
  .table-header.column-7 > :nth-child(2),
  .table-header.column-7 > :nth-child(3),
  .table-header.column-7 > :nth-child(7),
  .table-row.column-7 > :nth-child(2),
  .table-row.column-7 > :nth-child(3),
  .table-row.column-7 > :nth-child(7) {
    display: none;
  }

  /* Eltern-Ansicht: Betreff und Aktion ausblenden (5 Spalten -> 3 sichtbare) */
  .table-header.column-5,
  .table-row.column-5 {
    grid-template-columns: 1.6fr 1fr 140px 100px;
  }

  .table-header.column-5 > :nth-child(2),
  .table-header.column-5 > :nth-child(5),
  .table-row.column-5 > :nth-child(2),
  .table-row.column-5 > :nth-child(5) {
    display: none;
  }
}

@media (min-width: 600px)  and (max-width: 799px) {
  /* Parent: Filter kompakter (2 Spalten), Touch-Ziele größer */
  .notifications-page .filters {
    grid-template-columns: 100px 100px auto;
    align-items: end;
    gap: 6px;
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 12px;
    background: whitesmoke;
  }

  :deep(.filters .flatpickr-input) {
    height: 32px !important;
  }

  .notifications-page .filters .field--search {
    grid-column: auto;
  }

  .notifications-page .filters .field--status {
    grid-column: 1 / 3;
    grid-row: 2;
  }

  .notifications-page .filters .field--chat {
    grid-column: 3 / 3;
    grid-row: 2;
  }

  .notifications-page .filters .field--reset {
    grid-column: 1 / -1;
    grid-row: 3;
  }
}

.notif-card__actions {
  display: inline-flex;
  justify-content: flex-end;
  gap: 6px;
}

.notif-card__actions button {

}

/* Layout-Fix: Viewport <1200px → fixiere Layout-Spalten auf 240px + 1200px */
@media (max-width: 1199px) {
  :global(.layout) {
    grid-template-columns: 240px 1200px !important;
  }
}
</style>